import { Box, Flex, Skeleton, Text } from '@chakra-ui/react'
import { useMemo } from 'react'

import { useGetAnswerRate } from '../hooks/useGetAnswerRate'
import { useReportingI18n } from '../i18n'
import type { ReportingPhoneNumber } from '../types'
import { DEFAULT_METRIC_VALUE } from '../types'

interface TableRow {
  number: string
  answerRate: number
  answered: number
  missed: number
  total: number
}

interface InboundAnswerRateTableProps {
  startDate: string
  endDate: string
  pbxId: number
  selectedPhoneNumbers: ReportingPhoneNumber[]
}

const AnswerRateBar = ({ percentage }: { percentage: number }) => {
  return (
    <Flex alignItems="center" gap="8px" flex="1" minW="0">
      <Text
        fontFamily="Inter"
        fontSize="12px"
        fontWeight="500"
        lineHeight="16px"
        color="fg.default"
        flexShrink={0}
        minW="36px"
      >
        {percentage}%
      </Text>
      <Box
        flex="1"
        maxWidth="120px"
        minWidth="40px"
        height="8px"
        bg="bg.emphasized"
        borderRadius="full"
        overflow="hidden"
      >
        <Box
          width={`${percentage}%`}
          height="100%"
          bg="fg.success"
          borderRadius="full"
          transition="width 0.3s ease"
        />
      </Box>
    </Flex>
  )
}

const InboundAnswerRateTable = ({
  startDate,
  endDate,
  pbxId,
  selectedPhoneNumbers,
}: InboundAnswerRateTableProps) => {
  const { text } = useReportingI18n()

  const { data, isLoading, error } = useGetAnswerRate({
    request: {
      start: startDate,
      end: endDate,
      pbx: pbxId.toString(),
      ...(selectedPhoneNumbers.length > 0 && {
        phone_number: selectedPhoneNumbers.map((p) => p.number),
      }),
      group_by: 'phone_number',
    },
    enabled: !!startDate && !!endDate && pbxId > 0,
  })

  const tableData = useMemo<TableRow[]>(() => {
    const segments = data?.segments || []

    const rows = segments.map((segment) => {
      const answered = segment.counts.answered ?? DEFAULT_METRIC_VALUE
      const missed = segment.counts.rejected ?? DEFAULT_METRIC_VALUE
      const total = answered + missed
      const answerRate = total > 0 ? Math.round((answered / total) * 100) : 0

      return {
        number:
          segment.phoneNumber ||
          text('inboundAnswerRate.table.unknown', 'Unknown'),
        answerRate,
        answered,
        missed,
        total,
      }
    })

    if (selectedPhoneNumbers.length > 0) {
      const segmentNumbersSet = new Set(
        segments
          .map((s) => s.phoneNumber)
          .filter((num): num is string => !!num && num.length > 0),
      )

      const missingNumbers = selectedPhoneNumbers.filter((phoneNum) => {
        if (!phoneNum?.number || phoneNum.number.length === 0) {
          return false
        }
        return !segmentNumbersSet.has(phoneNum.number)
      })

      const missingRows = missingNumbers.map((phoneNum) => ({
        number: phoneNum.number,
        answerRate: 0,
        answered: 0,
        missed: 0,
        total: 0,
      }))

      const combinedRows = [...rows, ...missingRows]
      return combinedRows.sort((a, b) => b.total - a.total)
    }

    return rows.sort((a, b) => b.total - a.total)
  }, [data, selectedPhoneNumbers, text])

  const cellTextStyle = {
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: '500',
    lineHeight: '16px',
  }

  if (isLoading) {
    return <Skeleton height="400px" width="100%" borderRadius="10px" />
  }

  if (error) {
    return (
      <Box
        width="100%"
        padding="2rem"
        textAlign="center"
        border="1px solid"
        borderColor="border.default"
        borderRadius="10px"
      >
        <Text color="fg.muted">
          {text(
            'inboundAnswerRate.table.errorLoading',
            'Error loading phone number statistics',
          )}
        </Text>
      </Box>
    )
  }

  if (tableData.length === 0) {
    return (
      <Box
        width="100%"
        padding="2rem"
        textAlign="center"
        border="1px solid"
        borderColor="border.default"
        borderRadius="10px"
      >
        <Text color="fg.muted">
          {text(
            'inboundAnswerRate.table.noData',
            'No phone number data available',
          )}
        </Text>
      </Box>
    )
  }

  return (
    <Box
      width="100%"
      border="1px solid"
      borderColor="border.default"
      borderRadius="10px"
      overflow="hidden"
    >
      <Flex
        display="flex"
        padding="12px 16px"
        alignItems="center"
        gap="16px"
        alignSelf="stretch"
        borderBottom="1px solid"
        borderColor="border.default"
        bg="bg.subtle"
      >
        <Text {...cellTextStyle} color="fg.default" flex="1.5" minW="100px">
          {text('inboundAnswerRate.table.columns.number', 'Number')}
        </Text>
        <Text {...cellTextStyle} color="fg.default" flex="2" minW="100px">
          {text(
            'inboundAnswerRate.table.columns.answerRate',
            'Inbound Answer Rate',
          )}
        </Text>
        <Text {...cellTextStyle} color="fg.default" flex="0.8" minW="60px">
          {text('inboundAnswerRate.table.columns.answered', 'Calls Answered')}
        </Text>
        <Text {...cellTextStyle} color="fg.default" flex="0.8" minW="60px">
          {text('inboundAnswerRate.table.columns.missed', 'Missed Calls')}
        </Text>
        <Text {...cellTextStyle} color="fg.default" flex="0.8" minW="60px">
          {text('inboundAnswerRate.table.columns.total', 'Total Calls')}
        </Text>
      </Flex>

      <Box>
        {tableData.map((row, index) => (
          <Flex
            key={row.number}
            display="flex"
            padding="16px"
            alignItems="center"
            gap="16px"
            alignSelf="stretch"
            borderBottom={index < tableData.length - 1 ? '1px solid' : 'none'}
            borderColor="border.default"
            _hover={{ bg: 'bg.subtle' }}
          >
            <Text {...cellTextStyle} color="fg.default" flex="1.5" minW="100px">
              {row.number}
            </Text>
            <Box flex="2" minW="100px">
              <AnswerRateBar percentage={row.answerRate} />
            </Box>
            <Text {...cellTextStyle} color="fg.info" flex="0.8" minW="60px">
              {row.answered.toLocaleString()}
            </Text>
            <Text {...cellTextStyle} color="fg.error" flex="0.8" minW="60px">
              {row.missed.toLocaleString()}
            </Text>
            <Text {...cellTextStyle} color="fg.default" flex="0.8" minW="60px">
              {row.total.toLocaleString()}
            </Text>
          </Flex>
        ))}
      </Box>
    </Box>
  )
}

export default InboundAnswerRateTable
